import { FunctionComponent, useMemo, useState } from 'react';
import {
    FullWindowModal,
    FullWindowModalHeader,
    FullWindowModalBody,
    FullWindowModalCloseButton
} from 'components/UI/V3/Modals/FullWindowModal';
import { SelectionBar, SelectionBarOption } from 'components/UI/V3/SelectionBar';
import AutomationTemplateCard from './AutomationTemplateCard';
import { AUTOMATION_TEMPLATES, templatesByCategory, type AutomationTemplate } from './automationTemplates';
import styles from './AutomationTemplatesGallery.module.scss';

interface Props {
    onSelect: (template: AutomationTemplate) => void;
    onClose: () => void;
}

const ALL = 'all';

const AutomationTemplatesGallery: FunctionComponent<Props> = ({ onSelect, onClose }) => {
    const [selectedScope, setSelectedScope] = useState<string>(ALL);

    const scopeOptions: Array<SelectionBarOption<string>> = useMemo(() => {
        const scopes = Array.from(new Set(AUTOMATION_TEMPLATES.map(t => t.serviceScope)))
            .filter(scope => scope !== 'All Services')
            .sort();

        return [{ id: ALL, name: 'All' }, ...scopes.map(scope => ({ id: scope, name: scope }))];
    }, []);

    const groups = useMemo(() => {
        const all = templatesByCategory();
        if (selectedScope === ALL) return all;

        return all
            .map(group => ({
                ...group,
                templates: group.templates.filter(
                    t => t.serviceScope === selectedScope || t.serviceScope === 'All Services'
                )
            }))
            .filter(group => group.templates.length > 0);
    }, [selectedScope]);

    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.topBar}>
                    <img
                        className={styles.logo}
                        src="/images/logo/logo.png"
                        alt="Cuddles"
                    />
                    <h1 className={styles.title}>Template Gallery</h1>
                    <FullWindowModalCloseButton
                        onClick={onClose}
                        ariaLabel="Close gallery"
                        className={styles.closeButton}
                    />
                </div>
            </FullWindowModalHeader>

            <div className={styles.filterRow}>
                <SelectionBar
                    options={scopeOptions}
                    selectedId={selectedScope}
                    onSelect={setSelectedScope}
                    className={styles.selectionBar}
                />
            </div>

            <FullWindowModalBody>
                <div className={styles.content}>
                    {groups.map(group => (
                        <section
                            key={group.category}
                            className={styles.categorySection}
                        >
                            <h3 className={styles.categoryTitle}>{group.label}</h3>
                            <div className={styles.templatesGrid}>
                                {group.templates.map(template => (
                                    <AutomationTemplateCard
                                        key={template.id}
                                        template={template}
                                        onUse={() => onSelect(template)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </FullWindowModalBody>
        </FullWindowModal>
    );
};

export default AutomationTemplatesGallery;
